// src/services/aiChat.js — what happens between typing @ai and reading an answer.
//
// The whole sequence, in one place, because it is a sequence and its steps have
// to happen in an order:
//
//   1. the question is sent like any other message, so it is sealed, relayed
//      and previewed exactly like any other message
//   2. the chat is marked pending, so every other device — yours and theirs,
//      phone and browser — shows the same thinking row
//   3. the model is asked
//   4. the answer is sent like any other message, carrying `isAI`
//   5. the marker is cleared, whatever happened in between
//
// 🟥 STEP 5 IS IN A `finally` FOR THE REASON THE PHONE LEARNED THE HARD WAY: it
//    removed its thinking row only on the success path, so a refused quota or a
//    dropped connection left a bubble pulsing in the conversation until the
//    chat was closed and reopened. Whatever happens, the marker comes off.
//
// 🟥 AND THE REQUEST DIES WITH THE TAB. The client that asked is the client
//    that answers — the phone works the same way — so closing the browser
//    before the answer arrives means no answer is ever written. That is why the
//    marker expires on its own rather than trusting anyone to clear it.

import { doc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { sendTextMessage } from './messages';
import { askAllo, askText, withMention } from './ai';
import { aiPendingWrite } from './aiPending';
/* ✦ who is in the room, described the same way the phone describes it */
import { sceneFor } from './aiIdentity';
/* ✦ one description of a quoted message, read by both clients — see aiContext */
import { buildAiReplyContext } from './aiContext';

/** Mark the chat as waiting on Allo AI. Best effort: a failed marker is a missing indicator, not a failed question. */
export async function markAiPending(chatId, uid, kind = 'text') {
  try {
    await updateDoc(doc(db, 'chats', chatId), {
      aiPending: aiPendingWrite(uid, kind, serverTimestamp(), 'web'),
    });
  } catch { /* the answer still lands; only the indicator is lost */ }
}

export async function clearAiPending(chatId) {
  try {
    await updateDoc(doc(db, 'chats', chatId), { aiPending: deleteField() });
  } catch { /* it expires on its own — see services/aiPending */ }
}

/**
 * Send an @ai message and answer it.
 *
 * @param send  how to put a message in this chat. Injected rather than
 *              imported so the caller keeps its reply/link handling, and so
 *              this can be tested without a database.
 * @returns what happened, for the caller to report. Never throws.
 */
export async function askInChat({
  chatId, participants, senderId, senderName, isGroup, groupName, otherNames = [],
  text, reply, send = sendTextMessage,
}) {
  const question = withMention(text);
  const prompt = askText(text);

  /* 🟥 THE QUESTION IS SENT FIRST, AND ITS FAILURE ENDS THIS. Asking the model
     about a message that never made it into the conversation would put an
     answer in the chat with nothing to answer — and the person would have no
     idea what they were reading. */
  let sent;
  try {
    sent = await send({ chatId, participants, senderId, text: question, isGroup, reply });
  } catch {
    return { ok: false, reason: 'send_failed' };
  }

  if (!prompt) {
    /* "@ai" and nothing else. The mention is in the conversation, which is what
       the person typed; there is simply nothing to ask. */
    return { ok: false, reason: 'empty', question: sent };
  }

  await markAiPending(chatId, senderId, 'text');
  try {
    /* 🟥 THE MODEL IS TOLD WHOSE QUESTION THIS IS AND WHO ELSE WILL READ THE
       ANSWER, from the same module the phone uses. Without it a group question
       is answered to nobody in particular, and the asker's own name is unknown
       — which is what "what do you think of Abdullah's question" needs. */
    /* ══ 🤖 🟥 `reply` WENT ONTO THE STORED MESSAGE AND NEVER INTO THE PROMPT.
       The browser has the quoted message in hand the whole time — it hands it to
       send() below so the bubble draws the quote — and still asked the model the
       bare question. Same bug as the phone, same fix, and deliberately the same
       FUNCTION: a phone and a browser can be asking about one quoted message in
       one group, and two descriptions of it are two answers to one question. */
    let _prompt = prompt;
    try {
      if (reply && reply.replyTo) {
        const ctx = buildAiReplyContext(reply.replyTo, {
          senderName: reply.replyTo.senderName,
          isMine: reply.replyTo.senderId === senderId,
        });
        if (ctx) _prompt = `${ctx}\n\nTheir question: ${prompt}`;
      }
    } catch { /* a missing quote is not a reason to refuse the question */ }

    const answer = await askAllo(_prompt, {
      userName: senderName,
      people: sceneFor({ me: senderName, others: otherNames, isGroup: !!isGroup, groupName }),
    });
    if (!answer.ok || !answer.content) return { ok: false, reason: 'ai_failed', question: sent };

    /* 🟢 THE SAME SEND PATH AS EVERY OTHER MESSAGE. Sealed, relayed over the
       socket so the other device is actually notified, and reflected in the
       chat list — none of which an ad-hoc `addDoc` would do. `isAI` rides along
       as ordinary metadata, which is what the badge reads. */
    const written = await send({
      chatId, participants, senderId, isGroup,
      text: cleanForChat(answer.content),
      extraFields: { isAI: true },
    });
    return { ok: true, question: sent, answer: written };
  } catch {
    return { ok: false, reason: 'ai_failed', question: sent };
  } finally {
    await clearAiPending(chatId);
  }
}

/**
 * The model writes markdown; a chat bubble does not read it.
 *
 * 🟢 THE SAME FOUR RULES THE PHONE APPLIES, so the same answer reads the same
 *    way in both clients rather than showing its asterisks in one of them.
 */
export function cleanForChat(s) {
  return String(s || '')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/^#+\s/gm, '')
    .replace(/^\*\s/gm, '• ')
    .trim();
}
